import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { supabase } from "../../../supabase";
import { fetchOwnProfile } from "../../../utils/userProfile";
import { FiUser, FiMail, FiPhone, FiCalendar, FiCreditCard, FiTag, FiGift, FiShoppingBag, FiHeadphones, FiSettings, FiChevronRight, FiLogOut } from "react-icons/fi";
import { ProfileSkeleton } from "../../../components/ui/SkeletonCard";
import "./Profile.css";
import '../../../styles/global.css';
import { useNavigate } from "react-router-dom";
import { useParkSettings } from "../../../utils/parkSettings";

const CACHE_KEY = 'pws_user_cache';

function getCachedUser() {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY)); } catch { return null; }
}

export default function Profile() {
  const { t, i18n } = useTranslation('profile');
  const { pointsPercent } = useParkSettings();
  const cached = getCachedUser();
  const [userData, setUserData] = useState(cached);
  const [, setLoading] = useState(!cached);
  const [authChecked, setAuthChecked] = useState(false);
  const [error, setError] = useState(null);
  const [memberId, setMemberId] = useState(cached?.memberId ?? null);
  const navigate = useNavigate();

  const generateMemberId = () => {
    const digits = Math.floor(10000 + Math.random() * 90000);
    return `M${digits}`;
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setAuthChecked(true);
      const user = session?.user || null;
      if (user) {
        localStorage.setItem('pws_auth', '1');
        try {
          const data = await fetchOwnProfile(user.id);
          if (data) {
            setUserData(data);
            localStorage.setItem(CACHE_KEY, JSON.stringify(data));
            if (data.memberId) {
              setMemberId(data.memberId);
            } else {
              const newId = generateMemberId();
              setMemberId(newId);
              await supabase.from('users').update({ member_id: newId }).eq('auth_user_id', user.id);
              localStorage.setItem(CACHE_KEY, JSON.stringify({ ...data, memberId: newId }));
            }
          } else {
            setError(t('userDataNotFound'));
          }
        } catch (err) {
          setError(t('errorFetchingUserData', { message: err.message }));
        }
      } else {
        // Only redirect if we don't have cached data AND no auth flag
        // (i.e., truly logged out, not just waiting for the session to restore)
        const hasCache = !!getCachedUser();
        const hasAuthFlag = localStorage.getItem('pws_auth') === '1';

        if (!hasCache && !hasAuthFlag) {
          // Definitely logged out
          localStorage.removeItem(CACHE_KEY);
          navigate("/LogIn", { replace: true });
        } else if (hasAuthFlag && !hasCache) {
          // Has flag but no cache — wait briefly for auth to complete
          setTimeout(async () => {
            const { data: { session: currentSession } } = await supabase.auth.getSession();
            if (!currentSession) {
              localStorage.removeItem('pws_auth');
              localStorage.removeItem(CACHE_KEY);
              navigate("/LogIn", { replace: true });
            }
          }, 1000);
          // Don't return early - allow loading to clear
        }
        // else: has cache, stay on page and let this resolve in background
      }
      setLoading(false);
    });
    return () => subscription.unsubscribe();
  }, [navigate, t]);
  const handleLogout = async () => {
    try {
      localStorage.removeItem('pws_auth');
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem('productQuantities');
      localStorage.removeItem('cartItems');
      localStorage.removeItem('pws_ticket_summary');
      localStorage.removeItem('pws_kk_summary');
      localStorage.removeItem('discountCode');
      localStorage.removeItem('discountId');
      localStorage.removeItem('discountAmount');
      await supabase.auth.signOut();
      navigate("/login");
    } catch (err) {
      toast.error(t('logoutFailed', { message: err.message }));
    }
  };

  // If we have userData (from cache or fresh), render it immediately
  // Only show skeleton when we have no data at all
  if (!userData && !authChecked) return <ProfileSkeleton />;
  if (!userData) return <ProfileSkeleton />;
  if (error) return <p>{error}</p>;

  const points = userData.points || 0;

  const formatDate = (createdAt) => {
    if (!createdAt) return null;
    const date = new Date(createdAt);
    if (Number.isNaN(date.getTime())) return String(createdAt);
    return date.toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'nb-NO', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const menuMine = [
    { icon: FiTag,       color: 'yellow', label: t('menuTicketsLabel'), sub: t('menuTicketsSub'), path: '/tickets' },
    { icon: FiCreditCard,color: 'purple', label: t('menuKlippekortLabel'), sub: t('menuKlippekortSub'), path: '/klippekort-redeem' },
    { icon: FiGift,      color: 'green',  label: t('menuCouponsLabel'), sub: t('menuCouponsSub'), path: '/coupons' },
    { icon: FiShoppingBag, color: 'blue', label: t('menuOrdersLabel'), sub: t('menuOrdersSub'), path: '/orders' },
  ];
  const menuSupport = [
    { icon: FiHeadphones, color: 'teal', label: t('menuSupportLabel'), sub: t('menuSupportSub'), path: '/support' },
    { icon: FiSettings,   color: 'gray', label: t('menuSettingsLabel'), sub: t('menuSettingsSub'), path: '/Settings' },
  ];

  return (
    <div className="profile-wrapper">
      <div className="profile-scroll">

        {/* Hero */}
        <div className="profile-hero">
          <div className="profile-avatar"><FiUser size={38} /></div>
          <div className="profile-name">{userData.name?.toUpperCase()}</div>
          {memberId && <div className="profile-ref-badge">{memberId}</div>}
          <div className="profile-meta">
            <div className="profile-meta-row">
              <FiMail size={14} /><span>{t('emailLabel')}</span><strong>{userData.email}</strong>
            </div>
            {userData.phone && (
              <div className="profile-meta-row">
                <FiPhone size={14} /><span>{t('phoneLabel')}</span><strong>{userData.phone}</strong>
              </div>
            )}
            {userData.createdAt && (
              <div className="profile-meta-row">
                <FiCalendar size={14} /><span>{t('memberSinceLabel')}</span><strong>{formatDate(userData.createdAt)}</strong>
              </div>
            )}
          </div>
        </div>

        {/* Points */}
        <div
          className="profile-points-card"
          role="button"
          tabIndex={0}
          onClick={() => { window.navigator.vibrate?.(10); navigate('/coupons?tab=premier'); }}
          onKeyPress={e => { if (e.key === 'Enter' || e.key === ' ') navigate('/coupons?tab=premier'); }}
        >
          <div className="profile-points-label">{t('yourPoints')}</div>
          <div className="profile-points-value">{points.toFixed(2)}</div>
          <div className="profile-points-sub">{t('earnPointsSub', { percent: pointsPercent })}</div>
          <div className="profile-points-cta">{t('seeRewards')} <FiChevronRight size={14} /></div>
        </div>

        {/* Mine ting */}
        <div className="profile-section-label"><span>{t('sectionMyStuff')}</span></div>
        <div className="profile-menu-card">
          {menuMine.map(({ icon: Icon, color, label, sub, path }) => (
            <div
              key={path}
              className="profile-menu-row"
              role="button"
              tabIndex={0}
              onClick={() => { window.navigator.vibrate?.(10); navigate(path); }}
              onKeyPress={e => { if (e.key === 'Enter' || e.key === ' ') navigate(path); }}
            >
              <div className={`profile-menu-icon profile-menu-icon--${color}`}><Icon size={17} /></div>
              <div className="profile-menu-text"><h4>{label}</h4><p>{sub}</p></div>
              <FiChevronRight size={16} className="profile-menu-arrow" />
            </div>
          ))}
        </div>

        {/* Support & konto */}
        <div className="profile-section-label"><span>{t('sectionSupportAccount')}</span></div>
        <div className="profile-menu-card">
          {menuSupport.map(({ icon: Icon, color, label, sub, path }) => (
            <div
              key={path}
              className="profile-menu-row"
              role="button"
              tabIndex={0}
              onClick={() => { window.navigator.vibrate?.(10); navigate(path); }}
              onKeyPress={e => { if (e.key === 'Enter' || e.key === ' ') navigate(path); }}
            >
              <div className={`profile-menu-icon profile-menu-icon--${color}`}><Icon size={17} /></div>
              <div className="profile-menu-text"><h4>{label}</h4><p>{sub}</p></div>
              <FiChevronRight size={16} className="profile-menu-arrow" />
            </div>
          ))}
        </div>

        {/* Logout */}
        <button className="profile-logout-btn" onClick={() => { window.navigator.vibrate?.(10); handleLogout(); }}>
          <FiLogOut size={18} />
          {t('logOut')}
        </button>

      </div>
    </div>
  );
}
